import { InboxOutlined } from "@ant-design/icons"
import type { UploadFile } from "antd"
import {
  App,
  Form,
  Input,
  Modal,
  Progress,
  Select,
  Typography,
  Upload
} from "antd"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  createProjectDocument,
  getProjectDocumentFilterOptions
} from "../../../../api/project-documents.services.ts"
import type { ProjectDocumentTypeOption } from "../../../../types/project-documents.types.ts"

const { Text } = Typography

interface UploadDocumentModalProps {
  projectId: string
  onClose: () => void
  onUploaded: () => void
}

interface FormValues {
  file_name?: string
  file_type?: number
}

export function UploadDocumentModal({
  projectId,
  onClose,
  onUploaded
}: UploadDocumentModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const [types, setTypes] = useState<ProjectDocumentTypeOption[]>([])
  const [files, setFiles] = useState<UploadFile[]>([])
  const [uploaded, setUploaded] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    void getProjectDocumentFilterOptions().then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setTypes(
        (response.data.file_type ?? []).filter((option) =>
          option.visible_in?.includes("form")
        )
      )
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Renaming on upload only makes sense for a single file; with several, each
  // keeps its own name and the row action renames afterwards.
  const isSingle = files.length === 1

  const handleSubmit = async () => {
    const values = await form.validateFields()
    if (!files.length) return

    const typed = values.file_type ? { file_type: values.file_type } : {}
    const chosenName = values.file_name?.trim()

    setUploaded(0)
    let done = 0
    for (const [index, entry] of files.entries()) {
      const selected = entry.originFileObj
      if (!selected) continue

      const response = await createProjectDocument({
        lead: projectId,
        file_name: isSingle && chosenName ? chosenName : entry.name,
        file: selected,
        ...typed
      })
      if (response.ok) done += 1
      setUploaded(index + 1)
    }
    setUploaded(null)

    if (!done) {
      message.error(t("projectDocuments.upload.failed"))
      return
    }
    if (done < files.length) {
      message.warning(
        t("projectDocuments.upload.partial", { done, total: files.length })
      )
    } else {
      message.success(t("projectDocuments.upload.succeeded", { count: done }))
    }

    onUploaded()
    onClose()
  }

  const isSaving = uploaded !== null

  return (
    <Modal
      open
      title={t("projectDocuments.upload.title")}
      okText={t("projectDocuments.upload.submit")}
      onOk={() => void handleSubmit()}
      onCancel={onClose}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: !files.length }}
      cancelButtonProps={{ disabled: isSaving }}
    >
      <Form form={form} layout={"vertical"} requiredMark={false}>
        <Form.Item
          label={t("projectDocuments.upload.file")}
          required
          validateStatus={files.length ? undefined : "warning"}
        >
          <Upload.Dragger
            multiple
            beforeUpload={() => false}
            fileList={files}
            disabled={isSaving}
            onChange={({ fileList }) => {
              setFiles(fileList)
              if (fileList.length === 1 && !form.getFieldValue("file_name")) {
                form.setFieldValue("file_name", fileList[0].name)
              }
              if (fileList.length !== 1) {
                form.setFieldValue("file_name", undefined)
              }
            }}
          >
            <p className={"ant-upload-drag-icon"}>
              <InboxOutlined />
            </p>
            <Text>{t("projectDocuments.upload.dragHint")}</Text>
            <br />
            <Text type={"secondary"}>
              {t("projectDocuments.upload.dragSubHint")}
            </Text>
          </Upload.Dragger>
        </Form.Item>

        {isSingle && (
          <Form.Item
            name={"file_name"}
            label={t("projectDocuments.upload.name")}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("projectDocuments.upload.nameRequired")
              }
            ]}
          >
            <Input disabled={isSaving} />
          </Form.Item>
        )}

        {files.length > 1 && (
          <Text type={"secondary"}>
            {t("projectDocuments.upload.nameHint")}
          </Text>
        )}

        <Form.Item name={"file_type"} label={t("projectDocuments.upload.type")}>
          <Select
            allowClear
            disabled={isSaving}
            placeholder={t("projectDocuments.upload.typePlaceholder")}
            options={types.map(({ label, value }) => ({ label, value }))}
          />
        </Form.Item>

        {isSaving && (
          <Progress
            percent={Math.round(((uploaded ?? 0) / files.length) * 100)}
            status={"active"}
          />
        )}
      </Form>
    </Modal>
  )
}
